import { Dispatch } from "redux";
import { notification } from "antd";
import { AxiosError } from "axios";
import { GPSInsightsAxios } from "../Contracts/Axios/GPSInsightsAxios";
import {
    IFrequentlyVisitedPlace,
    IGPSGetJobResultParsed,
    IRootReducer,
} from "../Models/Models";
import { IBrand, IAnalysisItem } from "../Models/Models";
import { IGCPBigQueryJob } from "../Models/IGCPBigQueryJob";
import { GPSInsightsContainerView } from "../Enums/Enums";
import { setInsightsCollaborating, setReportsDates } from "./ReportsActions";
import { ISubmitMobilyticsJobRequest } from "../Models/Requests/ISubmitMobilyticsJobRequest";
import { IGetJobsRequest } from "../Models/Requests/IGetJobsRequest";
import {
    SET_GPS_INSIGHT_RESULTS,
    RESET_GPS_INSIGHT_RESULTS,
    GET_BRAND_SUCCESS,
    SET_LOADING_BRAND,
    GET_JOBS_SUCCESS,
    SET_LOADING_JOBS,
    ADD_RUNNING_INSIGHT_TO_LIST,
    ADD_FINISHED_INSIGHT_TO_LIST,
    REMOVE_FINISHED_INSIGHT_FROM_RUNNING_INSIGHTS,
    CLEAR_RUNNING_AND_FINISHED_INSIGHTS_LISTS,
    SELECT_GPS_INSIGHT_FOR_REPORT_VIEW,
    SET_CURRENT_GPS_INSIGHT_VIEW,
    CLEAR_FINISHED_INSIGHTS_LISTS,
    DELETE_REPORT_SUCCESS,
    START_GPS_MOBILE_INSIGHTS_REPORT,
    SET_LOADING_GPS_MOBILE_INSIGHTS_REPORT,
    SET_GPS_INSIGHT_MODAL_VISIBLE,
    SET_LOADING_SAVE_TO_SCOUT,
    SAVE_TO_SCOUT_SUCCESS,
    SET_POLYGON_CENTROID,
    SET_MOBILYTICS_SCENARIOS,
    SET_FVP_MARKERS,
    SET_SELECTED_FVP,
    UPDATE_GPS_INSIGHT_JOB,
    PROCESS_JOBS_SUCCESS,
} from "./Types";
import { cleanMap } from "./ReportMapActions";
import { IApiResult } from "../Models/IApiResult";
import { IMobilyticsScenarioLite } from "../Models/IMobilyticsScenarioLite";
import { IMapMarker } from "../Models/IMapMarker";
import { mapGcpJobsToAnalysisItems } from "../Util/ReportUtil";
import { AnalysisStatus } from "../Enums/AnalysisStatus";
import { IProcessJobsResult } from "../Models/IProcessJobsResult";
import { currentAppSelector } from "../View/Products/Selectors/AppSelectors";
import { App } from "../View/Products/Enums/App";

export const loadGPSInsightsSuccess = (data: IGPSGetJobResultParsed) => ({
    type: SET_GPS_INSIGHT_RESULTS,
    data: data,
});

const loadGPSInsightsFailure = () => ({
    type: SET_GPS_INSIGHT_RESULTS,
    data: null,
});

export const setPolygonCentroid = (centroid: { x: number; y: number }) => ({
    type: SET_POLYGON_CENTROID,
    data: centroid,
});

export const getResults = (jobId: string) => {
    return (dispatch: Dispatch<any>, getState: () => IRootReducer) => {
        dispatch(cleanMap());
        dispatch({ type: RESET_GPS_INSIGHT_RESULTS });
        setCurrentGpsInsightView(GPSInsightsContainerView.Insight);
        checkAndRemoveRunningAndFinishedInsightLists();

        const gpsInsightsAxios = new GPSInsightsAxios();
        const promise = gpsInsightsAxios.GetResults(jobId);
        promise
            .then((response: IGPSGetJobResultParsed) => {
                dispatch(loadGPSInsightsSuccess(response));

                for (let i = 0; i < response.data.length; i++) {
                    const jobId = response.job[i].jobId;
                    const numVisitors = response.data[i].mapData.length;
                    console.log(`${jobId}: ${numVisitors} visitor records`);
                }
            })
            .catch((error: AxiosError) => {
                notification.open({
                    type: "error",
                    message: `There was a problem while retrieving the requested insight. ${error.message}.`,
                });
                dispatch(loadGPSInsightsFailure);
            })
            .finally(() => {
                dispatch(setLoadingGpsMobileInsightsReport(false));
            });
    };
};

const getBrandSuccess = (data: IBrand) => ({
    type: GET_BRAND_SUCCESS,
    data: data,
});

const setLoadingBrand = (loading: boolean) => ({
    type: SET_LOADING_BRAND,
    data: loading,
});

export const getBrand = (term: string) => {
    return (dispatch: Dispatch<any>) => {
        dispatch(setLoadingBrand(true));

        const gpsInsightsAxios = new GPSInsightsAxios();
        gpsInsightsAxios
            .GetBrand(term)
            .then((response: IBrand) => {
                dispatch(getBrandSuccess(response));
            })
            .catch((error: string) => {
                notification.error({
                    message: "Error",
                    description: error,
                    duration: null, // user must close
                });
            })
            .finally(() => {
                dispatch(setLoadingBrand(false));
            });
    };
};

const getJobsSuccess = (data: IGCPBigQueryJob[]) => ({
    type: GET_JOBS_SUCCESS,
    data: data,
});

const processJobs = (data: IGCPBigQueryJob[]) => {
    return (dispatch: Dispatch<any>, getState) => {
        const state = getState();
        const currentApp = currentAppSelector(state);
        const finishedInsightsCopy: string[] = [...state.mobilyticsReportOutput.finishedInsights];
        /** copy of current reports */
        const previousReports: IAnalysisItem[] = [...state.mobilyticsReportOutput.mappedJobs];
        // TODO: GB
        /** Shouldn't have to do this logical OR check.
   * But for some reason the server call is returning a 204 - i.e. no content *
   */
        const incomingReports: IGCPBigQueryJob[] =
            data || [];
        const incomingMappedReports: IAnalysisItem[]
            = mapGcpJobsToAnalysisItems(incomingReports);

        // loop over the incoming jobs
        for (const incoming of incomingMappedReports) {
            // incomingMappedReports.forEach((incoming: IAnalysisItem) => {
            // loop over the previous jobs
            // for (const previous of previousReports) {
            const previous = previousReports.find(p => p.id === incoming.id);
            if (previous) {
                // previousReports.forEach((previous: IAnalysisItem) => {
                if (incoming.id === previous.id) {
                    // check status changes
                    if (
                        incoming.status === AnalysisStatus.Complete &&
                        previous.status !== AnalysisStatus.Complete
                    ) {
                        if (!finishedInsightsCopy.includes(incoming.id.toString())) {
                            finishedInsightsCopy.push(incoming.id.toString());
                        }
                    } else if (
                        incoming.status === AnalysisStatus.CompleteWithErrors &&
                        previous.status !== AnalysisStatus.CompleteWithErrors
                    ) {
                        if (currentApp !== App.Scout) {
                            notification.error({
                                message: `${incoming.name} had an error. If you continue to experience problems, please contact SCOUT Support.`,
                                duration: null, // user must close
                            });
                        }
                    } else if (
                        incoming.status === AnalysisStatus.TooFewRecords &&
                        previous.status !== AnalysisStatus.TooFewRecords
                    ) {
                        if (currentApp !== App.Scout) {
                            notification.error({
                                message: `${incoming.name} returned too few records. Please increase the polygon size and/or timeframe and try again.`,
                                duration: null, // user must close
                            });
                        }
                    }
                }
            }
        }

        const jobsSuccessData: IProcessJobsResult = {
            jobs: incomingReports,
            mappedJobs: incomingMappedReports,
            finishedInsights: finishedInsightsCopy,
        };
        dispatch({
            type: PROCESS_JOBS_SUCCESS,
            data: jobsSuccessData
        });
    }
}

const setLoadingJobs = (loading: boolean) => ({
    type: SET_LOADING_JOBS,
    data: loading,
});

export const getJobs = (isCollaborating: boolean, request: IGetJobsRequest) => {
    return (dispatch: Dispatch<any>) => {
        dispatch(setLoadingBrand(true));
        dispatch(setLoadingJobs(true));
        dispatch(setInsightsCollaborating(isCollaborating));
        dispatch(setReportsDates(request.startDate, request.endDate));

        const gpsInsightsAxios = new GPSInsightsAxios();

        if (isCollaborating) {
            gpsInsightsAxios
                .GetCompanyJobs(request)
                .then((response: IGCPBigQueryJob[]) => {
                    dispatch(getJobsSuccess(response));
                    dispatch(processJobs(response));
                })
                .catch((error: string) => {
                    notification.error({
                        message: "Error",
                        description: error,
                        duration: null, // user must close
                    });
                })
                .finally(() => {
                    dispatch(setLoadingJobs(false));
                    dispatch(setLoadingBrand(false));
                });
        } else {
            gpsInsightsAxios
                .GetUserJobs(request)
                .then((response: IGCPBigQueryJob[]) => {
                    dispatch(getJobsSuccess(response));
                    dispatch(processJobs(response));
                })
                .catch((error: string) => {
                    notification.error({
                        message: "Error",
                        description: error,
                        duration: null, // user must close
                    });
                })
                .finally(() => {
                    dispatch(setLoadingJobs(false));
                    dispatch(setLoadingBrand(false));
                });
        }
    };
};

export const deleteJob = (jobId: string) => {
    return (dispatch: Dispatch<any>, getState) => {
        dispatch(setLoadingJobs(true));

        const gpsInsightsAxios = new GPSInsightsAxios();
        gpsInsightsAxios
            .DeleteJob(jobId)
            .then((response: string) => {
                const job: IGCPBigQueryJob = getState().reports.allReports.find(
                    (job: IGCPBigQueryJob) => {
                        return job.jobId === response;
                    }
                );
                const report: IAnalysisItem = getState().reports.allReports.find(
                    (report: IAnalysisItem) => {
                        return report.id === job.id;
                    }
                );
                dispatch(deleteJobSuccess(report.id));
                setTimeout(function () {
                    (window as any).scout.actions.mobilyticsVisitorsReportDeleted(
                        job.jobId
                    );
                }, 1);
            })
            .catch((error: string) => {
                notification.error({
                    message:
                        "Failed to delete report. If you continue to see this message, please contact SCOUT Support.",
                    duration: null,
                });
            })
            .finally(() => {
                dispatch(setLoadingJobs(false));
            });
    };
};

export const addRunningInsight = (jobId: string) => {
    return (dispatch: Dispatch<any>, getState) => {
        const runningJobs = getState().gpsInsights.runningInsights;
        runningJobs.push(jobId);

        dispatch({
            type: ADD_RUNNING_INSIGHT_TO_LIST,
            data: runningJobs,
        });
    };
};

export const addFinishedInsight = (jobId: string) => {
    return (dispatch: Dispatch<any>, getState) => {
        const finishedJobs: string[] = [...getState().gpsInsights.finishedInsights];
        finishedJobs.push(jobId);
        removeFinishedInsightFromRunningInsights(jobId);

        dispatch({ type: ADD_FINISHED_INSIGHT_TO_LIST, data: finishedJobs });
    };
};

export const removeFinishedInsightFromRunningInsights = (jobId: string) => {
    return (dispatch: Dispatch<any>, getState) => {
        const runningJobs: string[] = [
            ...getState().gpsInsights.runningInsights,
        ].filter((job: string) => job !== jobId);

        dispatch({
            type: REMOVE_FINISHED_INSIGHT_FROM_RUNNING_INSIGHTS,
            data: runningJobs,
        });
    };
};

export const checkAndRemoveRunningAndFinishedInsightLists = () => {
    return (dispatch: Dispatch<any>, getState) => {
        const finishedInsights = getState().gpsInsights.finishedInsights;
        if (finishedInsights?.length > 0)
            dispatch({ type: CLEAR_RUNNING_AND_FINISHED_INSIGHTS_LISTS });
    };
};

export const selectGpsInsightForReportView = (job: IGCPBigQueryJob[]) => {
    return (dispatch: Dispatch<any>) => {
        dispatch({
            type: SELECT_GPS_INSIGHT_FOR_REPORT_VIEW,
            data: job,
        });
    };
};

export const setCurrentGpsInsightView = (
    gpsInsightView: GPSInsightsContainerView
) => {
    return (dispatch: Dispatch<any>) => {
        dispatch({ type: SET_CURRENT_GPS_INSIGHT_VIEW, data: gpsInsightView });
    };
};

export const resetGpsInsightResults = () => {
    return (dispatch: Dispatch<any>) => {
        dispatch({ type: RESET_GPS_INSIGHT_RESULTS });
    };
};

export const clearFinishedInsights = () => ({
    type: CLEAR_FINISHED_INSIGHTS_LISTS,
});

const deleteJobSuccess = (deletedBatchID: number) => ({
    type: DELETE_REPORT_SUCCESS,
    data: deletedBatchID,
});

export const startGpsMobileInsightsReport = () => ({
    type: START_GPS_MOBILE_INSIGHTS_REPORT,
    data: null,
});

export const setLoadingGpsMobileInsightsReport = (loading: boolean) => ({
    type: SET_LOADING_GPS_MOBILE_INSIGHTS_REPORT,
    data: loading,
});

export const setGpsInsightModalVisible = (visible: boolean) => ({
    type: SET_GPS_INSIGHT_MODAL_VISIBLE,
    data: visible,
});

export const submitGpsReport = (request: ISubmitMobilyticsJobRequest) => {
    return (dispatch: Dispatch<any>) => {
        dispatch(setLoadingGpsMobileInsightsReport(true));

        const gpsInsightsAxios = new GPSInsightsAxios();
        gpsInsightsAxios
            .SubmitGpsReport(request)
            .then((response: IApiResult<IGCPBigQueryJob>) => {
                if (response.success) {
                    dispatch(addRunningInsight(response.data.jobId));
                    notification.success({
                        message: "Submitted",
                        description: `Your Mobilytics Report is processing now. Check the reports section for status updates.`,
                    });
                } else {
                    notification.error({
                        message: "Error",
                        description: response.message,
                        duration: null, // user must close
                    });
                }
            })
            .catch((error: string) => {
                notification.error({
                    message: "Error",
                    description: error,
                    duration: null, // user must close
                });
            })
            .finally(() => {
                dispatch(setLoadingGpsMobileInsightsReport(false));
                dispatch(setGpsInsightModalVisible(false));
                (
                    window as any
                ).scout.interop.api.mobilyticsSubmission.closeClassicRightNav();
                (
                    window as any
                ).scout.interop.api.mobilyticsSubmission.clearGeographyStudyAreas();
            });
    };
};

export const setLoadingSaveToScout = (loading: boolean) => ({
    type: SET_LOADING_SAVE_TO_SCOUT,
    data: loading,
});

export const saveToScoutSuccess = (jobId: string) => ({
    type: SAVE_TO_SCOUT_SUCCESS,
    data: jobId,
});

export const setScenarios = (scenarios: IMobilyticsScenarioLite[]) => ({
    type: SET_MOBILYTICS_SCENARIOS,
    data: scenarios,
});

export const saveToScout = (job: IGCPBigQueryJob) => {
    return (dispatch: Dispatch<any>) => {
        dispatch(setLoadingSaveToScout(true));

        const gpsInsightsAxios = new GPSInsightsAxios();
        gpsInsightsAxios
            .SaveToScout(job.jobId)
            .then((response: boolean) => {
                dispatch(saveToScoutSuccess(job.jobId));
                notification.success({
                    message: "Saved",
                    description:
                        "Actual visitors and potential visitors from this Mobilytics analysis are now available to view on the SCOUT map. You can find it in the Mobilytics Visitors section, accessible from the left nav bar.",
                });
                (window as any).scout.actions.refreshMobilyticsVisitorsList();
            })
            .finally(() => {
                dispatch(setLoadingSaveToScout(false));
            });
    };
};

export const updatePolygonName = (jobId: string, newName: string) => {
    return new Promise<IGCPBigQueryJob>((resolve, reject) => {
        const gpsInsightsAxios = new GPSInsightsAxios();
        gpsInsightsAxios
            .updatePolygonName(jobId, newName)
            .then((newJob: IGCPBigQueryJob) => {
                resolve(newJob);
            })
            .catch((error) => {
                reject(error);
            });
    });
};

export const updateJobTitle = (jobId: string, newReportName: string) => {
    return new Promise<IGCPBigQueryJob>((resolve, reject) => {
        const gpsInsightsAxios = new GPSInsightsAxios();
        gpsInsightsAxios
            .updateJobTitle(jobId, newReportName)
            .then((result) => {
                resolve(result);
            })
            .catch((error) => {
                reject(error);
            });
    });
};

export const updateMobilyticsJob = (job: IGCPBigQueryJob) => ({
    type: UPDATE_GPS_INSIGHT_JOB,
    data: job,
});

export const setFvpMapMarkers = (markers: IMapMarker[]) => ({
    type: SET_FVP_MARKERS,
    data: markers,
});

export const setSelectedFVP = (fvp: IFrequentlyVisitedPlace) => ({
    type: SET_SELECTED_FVP,
    data: fvp,
});